import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Container, Button, Typography, Checkbox, FormGroup, FormControlLabel,
    Box, Paper, Snackbar, Stepper, Step, StepLabel, TextField
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createAndStoreEmployeeSignUpInvite, fetchServicesBySiteId, getBusinessConfigById } from '../../firebase/firebase-delegate';
import { BusinessConfig, Service } from '../../types';
import ErrorDialog from '../../components/global/ErrorDialog';
import LoadingSpinner from '../../components/global/LoadingSpinner';

const SuperAdminAddEmployeePage = () => {
    const { siteId } = useParams<{ siteId: string }>();
    const [isAdmin, setIsAdmin] = useState(false);
    const [selectedServices, setSelectedServices] = useState<string[]>([]);
    const [inviteLink, setInviteLink] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [customDomainHost, setCustomDomainHost] = useState('');
    const steps = ['1', '2', '3', '4'];

    const servicesQuery = useQuery({
        queryKey: ['services', siteId],
        queryFn: () => fetchServicesBySiteId(siteId!),
        enabled: !!siteId,
    });

    const businessConfigQuery = useQuery<BusinessConfig | null>({
        queryKey: ['businessConfig', siteId],
        enabled: !!siteId,
        queryFn: () => getBusinessConfigById(siteId!)
    });

    const createInviteMutation = useMutation({
        mutationFn: async () => {
            if (!siteId) throw new Error("Missing site ID");
            return await createAndStoreEmployeeSignUpInvite(isAdmin, selectedServices, siteId, customDomainHost.trim());;
        },
        onSuccess: (link) => setInviteLink(link),
        onError: () => setShowErrorDialog(true),
    });

    const isInviteLoading = createInviteMutation.isPending;

    const handleServiceToggle = (serviceId: string) => {
        setSelectedServices((prev) =>
            prev.includes(serviceId) ? prev.filter(id => id !== serviceId) : [...prev, serviceId]
        );
    };

    const handleCopyLink = async () => {
        if (inviteLink) {
            await navigator.clipboard.writeText(inviteLink);
            setSnackbarMessage('Invite link copied to clipboard!');
            setSnackbarOpen(true);
        }
    };

    const handleShare = async () => {
        if (inviteLink && navigator.share) {
            try {
                await navigator.share({ title: 'Employee Invite', text: 'Join our system using this invite link:', url: inviteLink });
            } catch (error) {
                console.error('Error sharing:', error);
            }
        } else {
            setSnackbarMessage('Sharing not supported on this device.');
            setSnackbarOpen(true);
        }
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <Box textAlign="center">
                        <Typography variant="overline">SELECT ROLE</Typography>
                        <Typography variant="body1" sx={{ mt: 1 }}>Admins can manage staff, services, and settings.</Typography>
                        <FormControlLabel
                            control={<Checkbox checked={isAdmin} onChange={() => setIsAdmin(!isAdmin)} />}
                            label="Grant Admin Access"
                            sx={{ mt: 2 }}
                        />
                    </Box>
                );
            case 1:
                return (
                    <Box textAlign="center">
                        <Typography variant="overline">SELECT SERVICES</Typography>
                        {servicesQuery.isLoading ? (
                            <LoadingSpinner />
                        ) : (
                            <FormGroup>
                                {servicesQuery.data?.map((service: Service) => (
                                    <FormControlLabel
                                        key={service.id}
                                        control={
                                            <Checkbox
                                                checked={selectedServices.includes(service.id)}
                                                onChange={() => handleServiceToggle(service.id)}
                                            />
                                        }
                                        label={service.title}
                                    />
                                ))}
                            </FormGroup>
                        )}
                    </Box>
                );
            case 2:
                return (
                    <Box textAlign="center">
                        <Typography variant="overline" display="block">CUSTOM DOMAIN</Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 2 }}>
                            This is the subdomain for the employee invite link.
                        </Typography>
                        <Box display="flex" alignItems="center" justifyContent="center">
                            <TextField
                                variant="outlined"
                                value={customDomainHost}
                                onChange={(e) => setCustomDomainHost(e.target.value)}
                                placeholder="example"
                                sx={{ width: 200 }}
                            />
                            <Typography sx={{ ml: 1 }}>.scheduify.com</Typography>
                        </Box>
                    </Box>
                );
            case 3:
                return (
                    <Box textAlign="center">
                        <Typography variant="overline">SEND INVITE</Typography>
                        {isInviteLoading ? (
                            <LoadingSpinner />
                        ) : inviteLink ? (
                            <Button variant="outlined" onClick={handleCopyLink} sx={{ mt: 2 }}>{inviteLink}</Button>
                        ) : null}
                        <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                            Expires in two hours
                        </Typography>
                    </Box>
                );
            default:
                return null;
        }
    };

    const handleNext = () => {
        if (currentStep === steps.length - 2) {
            createInviteMutation.mutate();
        }
        setCurrentStep(prev => prev + 1);
    };

    return (
        <Container maxWidth="sm">
            <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                <Typography variant="h5" align="center" fontWeight="bold" gutterBottom>
                    Add Employee (Site: {businessConfigQuery.data?.businessInfo.name})
                </Typography>

                <Stepper activeStep={currentStep} alternativeLabel sx={{ my: 2 }}>
                    {steps.map((_, idx) => <Step key={idx}><StepLabel /></Step>)}
                </Stepper>

                {renderStepContent()}

                <Box mt={3} display="flex" justifyContent="space-between">
                    <Button variant="outlined" disabled={currentStep === 0} onClick={() => setCurrentStep(prev => prev - 1)}>
                        Back
                    </Button>

                    {currentStep < steps.length - 1 && (
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleNext}
                            disabled={currentStep === 2 && !customDomainHost.trim()}
                        >
                            Next
                        </Button>
                    )}

                    {currentStep === steps.length - 1 && inviteLink && !isInviteLoading && (
                        <Button variant="contained" color="secondary" onClick={handleShare}>
                            Share
                        </Button>
                    )}
                </Box>
            </Paper>

            <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} message={snackbarMessage} />
            <ErrorDialog open={showErrorDialog} onClose={() => setShowErrorDialog(false)} message="Something went wrong. Please try again." />
        </Container>
    );
};

export default SuperAdminAddEmployeePage;
